/**
 * Cognito token storage, silent refresh, and session lifecycle.
 * Access tokens are refreshed ~5 minutes before expiry; authFetch retries once on 401.
 */

const REFRESH_BUFFER_MS = 5 * 60 * 1000;

let refreshTimerId = null;
let refreshInFlight = null;
let redirectingToLogin = false;

export const decodeJwtExpMs = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

export function getStoredTokens() {
  try {
    const raw = localStorage.getItem('cognito_tokens');
    if (raw) {
      const tokens = JSON.parse(raw);
      if (tokens && typeof tokens === 'object') return tokens;
    }
    const userRaw = localStorage.getItem('user');
    if (userRaw) {
      const user = JSON.parse(userRaw);
      if (user?.tokens) return user.tokens;
    }
  } catch {
    return null;
  }
  return null;
}

export function getStoredUserEmail() {
  try {
    const userRaw = localStorage.getItem('user');
    if (!userRaw) return null;
    const user = JSON.parse(userRaw);
    return user?.email ? String(user.email).trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

/** Cognito sub (UserSub) — required for REFRESH_TOKEN_AUTH SECRET_HASH when email is an alias. */
export function getStoredCognitoUsername() {
  try {
    const userRaw = localStorage.getItem('user');
    if (userRaw) {
      const user = JSON.parse(userRaw);
      if (user?.id) return String(user.id);
    }
  } catch {
    /* ignore */
  }

  const tokens = getStoredTokens();
  const token = tokens?.idToken || tokens?.accessToken;
  if (!token || typeof token !== 'string') return null;

  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload?.sub ? String(payload.sub) : null;
  } catch {
    return null;
  }
}

export function getAccessToken() {
  const tokens = getStoredTokens();
  return tokens?.accessToken || null;
}

export function getAccessTokenExpiryMs() {
  const tokens = getStoredTokens();
  if (!tokens?.accessToken) return null;
  if (tokens.expiresAt) return tokens.expiresAt;
  return decodeJwtExpMs(tokens.accessToken);
}

export function hasRefreshToken() {
  return Boolean(getStoredTokens()?.refreshToken);
}

/** True when a signed-in user session with a Cognito access token is present. */
export function isAuthenticated() {
  try {
    const userRaw = localStorage.getItem('user');
    if (!userRaw) return false;
    const user = JSON.parse(userRaw);
    if (!user?.email) return false;
  } catch {
    return false;
  }
  return Boolean(getAccessToken());
}

export function isAccessTokenFresh(bufferMs = REFRESH_BUFFER_MS) {
  const expMs = getAccessTokenExpiryMs();
  if (!expMs) return false;
  return expMs > Date.now() + bufferMs;
}

/** Session is restorable when a refresh token exists or access token is still valid. */
export function isAuthSessionValid() {
  try {
    const userRaw = localStorage.getItem('user');
    if (!userRaw) return false;
    const user = JSON.parse(userRaw);
    if (!user?.email) return false;
    if (hasRefreshToken()) return true;
    const expMs = getAccessTokenExpiryMs();
    return expMs != null && expMs > Date.now();
  } catch {
    return false;
  }
}

export function persistTokens(tokens) {
  if (!tokens?.accessToken) return;

  const expiresAt = tokens.expiresAt
    || (tokens.expiresIn
      ? Date.now() + Number(tokens.expiresIn) * 1000
      : decodeJwtExpMs(tokens.accessToken));

  const stored = {
    ...tokens,
    expiresAt: expiresAt || undefined,
  };

  try {
    localStorage.setItem('cognito_tokens', JSON.stringify(stored));
    const userRaw = localStorage.getItem('user');
    if (userRaw) {
      const user = JSON.parse(userRaw);
      user.tokens = stored;
      localStorage.setItem('user', JSON.stringify(user));
    }
    redirectingToLogin = false;
  } catch {
    /* ignore storage errors */
  }
}

export function clearAuthSession() {
  stopAuthSessionRefresh();
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('cognito_tokens');
  } catch {
    /* ignore */
  }
}

export function redirectToLogin() {
  if (redirectingToLogin) return;
  if (typeof window !== 'undefined' && window.location.pathname === '/login') return;
  redirectingToLogin = true;
  clearAuthSession();
  window.location.href = '/login';
}

export async function refreshAuthTokens() {
  if (refreshInFlight) return refreshInFlight;

  const email = getStoredUserEmail();
  const username = getStoredCognitoUsername();
  const tokens = getStoredTokens();
  if (!email || !username || !tokens?.refreshToken) {
    return false;
  }

  refreshInFlight = (async () => {
    try {
      const response = await fetch('/api/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          username,
          refreshToken: tokens.refreshToken,
        }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      if (!data?.tokens?.accessToken) return false;

      persistTokens(data.tokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function ensureFreshAccessToken() {
  if (isAccessTokenFresh()) return true;
  if (!hasRefreshToken()) return false;
  return refreshAuthTokens();
}

function scheduleTokenRefresh() {
  if (refreshTimerId != null) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }

  if (!hasRefreshToken()) return;

  const expMs = getAccessTokenExpiryMs();
  if (!expMs) return;

  const delay = Math.max(0, expMs - Date.now() - REFRESH_BUFFER_MS);
  refreshTimerId = setTimeout(async () => {
    refreshTimerId = null;
    const ok = await refreshAuthTokens();
    if (ok) {
      scheduleTokenRefresh();
    } else {
      redirectToLogin();
    }
  }, delay);
}

/** Refresh on load when access token is expired but refresh token is still valid. */
export async function bootstrapAuthSession() {
  if (!isAuthSessionValid()) return false;
  if (isAccessTokenFresh(0)) return true;
  if (!hasRefreshToken()) return false;
  return refreshAuthTokens();
}

export function startAuthSessionRefresh() {
  stopAuthSessionRefresh();

  if (!hasRefreshToken()) return () => {};

  const runInitialRefresh = async () => {
    if (!isAccessTokenFresh(0)) {
      const ok = await refreshAuthTokens();
      if (!ok) return;
    }
    scheduleTokenRefresh();
  };

  runInitialRefresh();

  return stopAuthSessionRefresh;
}

export function stopAuthSessionRefresh() {
  if (refreshTimerId != null) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
}
